// contexts/DragDropContext.tsx
/**
 * DragDropContext — planner drag-and-drop on @dnd-kit (UI Tier 3.1b).
 *
 * Replaces the HTML5 drag-event + hand-rolled long-press implementation
 * (hooks/useDragAndDrop) with dnd-kit primitives while keeping the same
 * drop semantics:
 *   - cross-semester move (availability check, remove-from-source)
 *   - same-semester positional reorder (index adjustment for the lifted card)
 *   - bookmarks as a non-positional zone (add + remove-from-source)
 *   - AP credits zone rejects drops (managed via the AP Credits modal)
 *
 * What dnd-kit buys us over the old path:
 *   - one pipeline for mouse, touch (200ms long-press), and keyboard drag
 *     (Space lifts, arrows move, Space drops, Escape cancels)
 *   - <DragOverlay> preview with a spring drop animation instead of a
 *     manually-positioned DOM node
 *   - auto-scroll while dragging near viewport edges
 *   - screen-reader announcements for lift/over/drop
 *
 * Exports:
 *   <PlannerDndProvider planOps={...}> — mounts DndContext + DragOverlay and
 *     provides drag state to the planner subtree.
 *   useDragDrop() — read drag state (dragOverSemester, isDropPosition, ...).
 *   <DraggableCourse> — render-prop wrapper a card uses to become a drag
 *     source (safe to render outside the provider: the bind is inert).
 *   <SemesterDropZone> — container div registered as a drop target.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  type DropAnimation,
} from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import type { PlanOperations } from '../hooks/useAcademicPlan';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Course data for drag operations. Intentionally has no index signature so
 * well-typed shapes like EnrolledCourse remain assignable (see the note on
 * structural typing in the pre-dnd-kit version of this module).
 */
export interface DragCourse {
  code: string;
  title?: string;
  credits?: number;
  /** Placeholder cards (#205) route cross-semester drops through moveCourse. */
  isPlaceholder?: boolean;
}

/**
 * Plan operations the drop executor needs.
 *
 * Derived from the canonical PlanOperations rather than hand-written, so the
 * signatures can't drift (F11 — App used to hand this to the provider through
 * an `as unknown as` cast that hid exactly that drift). addCourse/removeCourse
 * are required; the rest are optional so mocks and partial wirings stay valid.
 *
 * addCourse returns true/false = added/rejected; the drop executor gates source
 * removal on it (see executeDrop). Void is tolerated for legacy mocks.
 */
export type PlanOpsForDrag = {
  addCourse: (course: DragCourse, semester: string) => Promise<boolean | void>;
  removeCourse: (semester: string, index: number, skipConfirmation?: boolean) => void;
} & Partial<
  Pick<
    PlanOperations,
    'addCourseAtPosition' | 'reorderCourse' | 'addToBookmarks' | 'removeFromBookmarks' | 'moveCourse'
  >
> & {
  checkCourseAvailability?: (course: DragCourse, semester: string) => Promise<unknown>;
};

/** Payload carried on each draggable via dnd-kit's data option. */
interface DragSourceData {
  course: DragCourse;
  from: string;
  index: number;
}

/** What DraggableCourse hands to its child to wire the drag grip. */
export interface CourseDragBind {
  ref: (element: HTMLElement | null) => void;
  listeners: SyntheticListenerMap | undefined;
  attributes: Record<string, unknown>;
  isDragging: boolean;
}

export interface DragDropContextValue {
  activeCourse: DragCourse | null;
  activeFromSemester: string | null;
  dragOverSemester: string | null;
  isDragging: boolean;
  isDropPosition: (semester: string, index: number) => boolean;
}

// Legacy window guard retained: plan mutations are server-backed and a
// double-fired drop would double-add the course.
declare global {
  interface Window {
    _dropInProgress?: boolean;
  }
}

// =============================================================================
// CONSTANTS + PURE HELPERS
// =============================================================================

/** Zones that accept drops but have no insertion position. */
const NON_POSITIONAL_ZONES = ['bookmarks'];

/**
 * Insertion index within a drop container from the dragged card's vertical
 * center: before the first card whose midpoint is below it.
 */
export function calculateDropIndex(container: Element, centerY: number): number {
  const courseCards = container.querySelectorAll('[data-course-card]');
  let dropIndex = courseCards.length;

  for (let i = 0; i < courseCards.length; i++) {
    const rect = courseCards[i].getBoundingClientRect();
    if (centerY < rect.top + rect.height / 2) {
      dropIndex = i;
      break;
    }
  }

  return dropIndex;
}

export interface DropRequest {
  course: DragCourse;
  fromSemester: string;
  fromIndex: number;
  toSemester: string;
  /** Insertion index in the target zone; null for non-positional zones. */
  dropIndex: number | null;
}

/**
 * Execute a drop against planOps. Pure of React state — everything it needs
 * arrives in the request — so it is unit-testable without a DndContext.
 */
export async function executeDrop(planOps: PlanOpsForDrag, request: DropRequest): Promise<void> {
  if (window._dropInProgress) return;
  window._dropInProgress = true;

  const { course, fromSemester, fromIndex, toSemester, dropIndex } = request;

  try {
    const isFromBookmarks = fromSemester === 'bookmarks';
    const isSameSemester = fromSemester === toSemester;

    // Availability check for cross-semester moves. Placeholders are exempt
    // (#205): they have no offering semantics — a slot is "offered" in every
    // term — and their client-generated code would always miss the offerings
    // data, firing a bogus "not typically offered" warning (fourth
    // staging-rehearsal catch).
    if (
      toSemester !== 'bookmarks' &&
      !isSameSemester &&
      !course.isPlaceholder &&
      planOps.checkCourseAvailability
    ) {
      await planOps.checkCourseAvailability(course, toSemester);
    }

    // Same-semester positional reorder
    if (isSameSemester && !isFromBookmarks) {
      if (dropIndex === null) return;
      // Dropping onto its own slot (or the gap just below itself) is a no-op
      if (fromIndex === dropIndex || fromIndex === dropIndex - 1) return;
      // The lifted card still occupies fromIndex; compensate when moving down
      const adjustedIndex = fromIndex < dropIndex ? dropIndex - 1 : dropIndex;

      if (planOps.reorderCourse) {
        await planOps.reorderCourse(toSemester, fromIndex, adjustedIndex);
      } else {
        planOps.removeCourse(fromSemester, fromIndex, true);
        if (planOps.addCourseAtPosition) {
          await planOps.addCourseAtPosition(course, toSemester, adjustedIndex);
        } else {
          await planOps.addCourse(course, toSemester);
        }
      }
      return;
    }

    const removeFromSource = (): void => {
      if (isFromBookmarks) {
        planOps.removeFromBookmarks?.(fromIndex);
      } else {
        planOps.removeCourse(fromSemester, fromIndex, true);
      }
    };

    // Dropping to bookmarks
    if (toSemester === 'bookmarks') {
      if (isFromBookmarks) return;
      planOps.addToBookmarks?.(course);
      removeFromSource();
      return;
    }

    // Placeholder semester→semester drags go through the ONE-OP backend move
    // (#205): placeholder identity is plan-wide, so the add-then-remove
    // choreography below is structurally impossible for them — add_placeholder
    // rejects the code as a duplicate while the original still sits in the
    // source semester (the third staging-rehearsal catch). moveCourse also
    // closes the remove/add loss window. Position within the target semester
    // is append-at-end for now (the backend move has no position parameter).
    if (course.isPlaceholder && !isFromBookmarks && planOps.moveCourse) {
      await planOps.moveCourse(course, fromSemester, toSemester, fromIndex);
      return;
    }

    // Cross-zone drop into a semester. Only remove from the source once the add
    // has actually landed — a rejected add (duplicate course already in the
    // target, backend failure) returns false / throws, and removing anyway would
    // delete the dragged card with nothing added (silent data loss).
    let added: boolean;
    try {
      const result =
        dropIndex !== null && planOps.addCourseAtPosition
          ? await planOps.addCourseAtPosition(course, toSemester, dropIndex)
          : await planOps.addCourse(course, toSemester);
      // void (legacy mocks) counts as added; only explicit false rejects.
      added = result !== false;
    } catch {
      added = false;
    }
    if (added) removeFromSource();
  } finally {
    setTimeout(() => {
      window._dropInProgress = false;
    }, 100);
  }
}

// =============================================================================
// CONTEXT
// =============================================================================

const DragDropContext = createContext<DragDropContextValue | null>(null);
DragDropContext.displayName = 'DragDropContext';

export const useDragDrop = (): DragDropContextValue => {
  const context = useContext(DragDropContext);
  if (!context) {
    throw new Error('useDragDrop must be used within a PlannerDndProvider');
  }
  return context;
};

/**
 * Value-injection provider for tests and previews: forces drag state without
 * mounting a DndContext (no sensors, no overlay). Production code mounts
 * PlannerDndProvider instead.
 */
export const DragDropStateProvider = ({
  value,
  children,
}: {
  value: Partial<DragDropContextValue>;
  children: ReactNode;
}) => {
  const merged: DragDropContextValue = {
    activeCourse: null,
    activeFromSemester: null,
    dragOverSemester: null,
    isDragging: false,
    isDropPosition: () => false,
    ...value,
  };
  return <DragDropContext.Provider value={merged}>{children}</DragDropContext.Provider>;
};

// =============================================================================
// DRAG SOURCE + DROP ZONE PRIMITIVES
// =============================================================================

interface DraggableCourseProps {
  /** Unique within the provider, e.g. `${semester}::${index}` */
  id: string;
  course: DragCourse;
  from: string;
  index: number;
  disabled?: boolean;
  children: (bind: CourseDragBind) => ReactNode;
}

/**
 * Render-prop drag source. Rendered outside a DndContext (e.g. in isolated
 * component tests) the bind is inert but renders fine — dnd-kit's contexts
 * carry default values.
 */
export const DraggableCourse = ({
  id,
  course,
  from,
  index,
  disabled = false,
  children,
}: DraggableCourseProps) => {
  const data: DragSourceData = useMemo(
    () => ({ course, from, index }),
    [course, from, index]
  );
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id,
    data: data as unknown as Record<string, unknown>,
    disabled,
  });

  return (
    <>
      {children({
        ref: setNodeRef,
        listeners,
        attributes: attributes as unknown as Record<string, unknown>,
        isDragging,
      })}
    </>
  );
};

interface SemesterDropZoneProps {
  /** Zone id: a semester code or 'bookmarks'. */
  zone: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  'data-testid'?: string;
}

/** Drop target container. Renders a div carrying data-drop-zone. */
export const SemesterDropZone = ({
  zone,
  disabled = false,
  className,
  children,
  'data-testid': dataTestId,
}: SemesterDropZoneProps) => {
  const { setNodeRef } = useDroppable({ id: zone, disabled, data: { zone } });

  return (
    <div ref={setNodeRef} data-drop-zone={zone} data-testid={dataTestId} className={className}>
      {children}
    </div>
  );
};

/**
 * Animated reflow gap rendered at the projected insertion index — the cards
 * below it make room for the drop.
 */
export const DropPlaceholder = ({ height = 36 }: { height?: number }) => (
  <div
    data-testid="drop-placeholder"
    aria-hidden="true"
    className="rounded-card border-2 border-dashed border-primary/50 bg-primary/5 animate-placeholder-grow print-hide"
    style={{ '--placeholder-height': `${height}px` } as CSSProperties}
  />
);

// =============================================================================
// PROVIDER
// =============================================================================

/** Overlay preview: compact card echoing the grid's course row. */

// User-facing label for drag announcements/preview: a placeholder's code is
// an internal generated id — speak/show its title instead.
const dragLabel = (course?: DragCourse): string =>
  course?.isPlaceholder ? (course.title || 'placeholder') : (course?.code || 'course');

const DragPreviewCard = ({ course }: { course: DragCourse }) => (
  <div className="w-[220px] bg-surface-card rounded-card shadow-lg border-2 border-primary/50 px-3 py-2 opacity-95 cursor-grabbing">
    {/* A placeholder's code is a generated 'placeholder-…' id — internal
        identity, never user-facing. Lead with the title instead. */}
    <div className="font-semibold text-sm text-ink">
      {course.isPlaceholder ? course.title || 'Placeholder' : course.code || 'Course'}
    </div>
    {course.isPlaceholder ? (
      <div className="text-xs text-ink-2 truncate">Placeholder</div>
    ) : (
      course.title && <div className="text-xs text-ink-2 truncate">{course.title}</div>
    )}
  </div>
);

// Spring settle on drop (matches the motion tokens' --ease-spring)
const dropAnimation: DropAnimation = {
  duration: 280,
  easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

// Pointer/touch use pointerWithin (precise); keyboard has no pointer, so fall
// back to rect intersection of the moved overlay.
const collisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
};

const screenReaderInstructions = {
  draggable:
    'To pick up a course, press space or enter. Use the arrow keys to move it over a semester. ' +
    'Press space or enter again to drop, or press escape to cancel.',
};

interface PlannerDndProviderProps {
  planOps: PlanOpsForDrag;
  children: ReactNode;
}

export const PlannerDndProvider = ({ planOps, children }: PlannerDndProviderProps) => {
  const [activeCourse, setActiveCourse] = useState<DragCourse | null>(null);
  const [activeFromSemester, setActiveFromSemester] = useState<string | null>(null);
  const [dragOverSemester, setDragOverSemester] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<{ zone: string; index: number } | null>(null);

  // Mirrors for the drop handler (avoids stale-closure reads in onDragEnd)
  const activeSourceRef = useRef<DragSourceData | null>(null);
  const dropPositionRef = useRef<{ zone: string; index: number } | null>(null);

  const sensors = useSensors(
    // 6px activation distance keeps clicks/expand taps from starting drags
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Long-press semantics match the old touch path (200ms hold, 10px slop)
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 10 } }),
    useSensor(KeyboardSensor)
  );

  const clearDragState = useCallback((): void => {
    setActiveCourse(null);
    setActiveFromSemester(null);
    setDragOverSemester(null);
    setDropPosition(null);
    activeSourceRef.current = null;
    dropPositionRef.current = null;
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent): void => {
    const source = event.active.data.current as DragSourceData | undefined;
    if (!source) return;
    activeSourceRef.current = source;
    setActiveCourse(source.course);
    setActiveFromSemester(source.from);
    if (navigator.vibrate) navigator.vibrate(50);
  }, []);

  const handleDragMove = useCallback((event: DragMoveEvent): void => {
    const zone = (event.over?.id as string | undefined) ?? null;
    setDragOverSemester(zone);

    if (!zone || NON_POSITIONAL_ZONES.includes(zone)) {
      setDropPosition(null);
      dropPositionRef.current = null;
      return;
    }

    const translated = event.active.rect.current.translated;
    const container = document.querySelector(`[data-drop-zone="${zone}"]`);
    if (!translated || !container) return;

    const centerY = translated.top + translated.height / 2;
    const next = { zone, index: calculateDropIndex(container, centerY) };
    setDropPosition((prev) =>
      prev && prev.zone === next.zone && prev.index === next.index ? prev : next
    );
    dropPositionRef.current = next;
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent): Promise<void> => {
      const source = activeSourceRef.current;
      const zone = (event.over?.id as string | undefined) ?? null;
      const position = dropPositionRef.current;
      clearDragState();

      if (!source || !zone) return;

      try {
        await executeDrop(planOps, {
          course: source.course,
          fromSemester: source.from,
          fromIndex: source.index,
          toSemester: zone,
          dropIndex: position && position.zone === zone ? position.index : null,
        });
      } catch (error) {
        console.error('[DragAndDrop] Drop failed:', error);
      }
    },
    [planOps, clearDragState]
  );

  const isDropPosition = useCallback(
    (semester: string, index: number): boolean =>
      dropPosition?.zone === semester && dropPosition.index === index,
    [dropPosition]
  );

  const announcements: Announcements = useMemo(
    () => ({
      onDragStart({ active }) {
        const source = active.data.current as DragSourceData | undefined;
        return `Picked up ${dragLabel(source?.course)}.`;
      },
      onDragOver({ active, over }) {
        const source = active.data.current as DragSourceData | undefined;
        return over
          ? `${dragLabel(source?.course)} is over ${over.id === 'bookmarks' ? 'bookmarks' : `semester ${over.id}`}.`
          : `${dragLabel(source?.course)} is no longer over a drop target.`;
      },
      onDragEnd({ active, over }) {
        const source = active.data.current as DragSourceData | undefined;
        return over
          ? `${dragLabel(source?.course)} dropped on ${over.id === 'bookmarks' ? 'bookmarks' : `semester ${over.id}`}.`
          : `${dragLabel(source?.course)} drag cancelled.`;
      },
      onDragCancel({ active }) {
        const source = active.data.current as DragSourceData | undefined;
        return `Dragging ${dragLabel(source?.course)} cancelled.`;
      },
    }),
    []
  );

  const value = useMemo<DragDropContextValue>(
    () => ({
      activeCourse,
      activeFromSemester,
      dragOverSemester,
      isDragging: activeCourse !== null,
      isDropPosition,
    }),
    [activeCourse, activeFromSemester, dragOverSemester, isDropPosition]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={clearDragState}
      accessibility={{ announcements, screenReaderInstructions }}
    >
      <DragDropContext.Provider value={value}>
        {children}
        <DragOverlay dropAnimation={dropAnimation}>
          {activeCourse ? <DragPreviewCard course={activeCourse} /> : null}
        </DragOverlay>
      </DragDropContext.Provider>
    </DndContext>
  );
};

export default DragDropContext;
